import { z } from 'zod';

/* -------------------------------------------------------------------------- */
/*  Primitives                                                                */
/* -------------------------------------------------------------------------- */

export const CurrencySchema = z.enum(['USD', 'EUR', 'GBP', 'INR', 'AED']);
export const RoleSchema = z.enum(['customer', 'manager', 'admin']);
export const ProductStatusSchema = z.enum(['draft', 'active', 'archived']);
export const OrderStatusSchema = z.enum([
  'pending_payment',
  'paid',
  'fulfilled',
  'cancelled',
  'refunded',
]);

export type ProductStatus = z.infer<typeof ProductStatusSchema>;
export type OrderStatusValue = z.infer<typeof OrderStatusSchema>;
export type Role = z.infer<typeof RoleSchema>;

export const SlugSchema = z
  .string()
  .min(2)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase words separated by single hyphens');

export const MoneyMinorSchema = z
  .number()
  .int('Amounts must be integers in the currency’s minor unit')
  .min(0)
  .max(100_000_000);

export const EmailSchema = z.string().trim().toLowerCase().email().max(254);

/**
 * Password policy: length is the only rule that reliably correlates with
 * strength, so we require 12 characters and reject the handful of passwords
 * that show up in every credential-stuffing list. Complexity classes
 * (upper/lower/digit/symbol) are deliberately not enforced — they push users
 * towards `Password1!` and add no entropy.
 */
export const PasswordSchema = z
  .string()
  .min(12, 'Use at least 12 characters')
  .max(200)
  .refine(
    (value) => !['password1234', '123456789012', 'qwertyuiop12'].includes(value.toLowerCase()),
    'This password is too common',
  );

export const PaginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(24),
  cursor: z.string().max(512).optional(),
});

/* -------------------------------------------------------------------------- */
/*  Auth                                                                      */
/* -------------------------------------------------------------------------- */

export const RegisterSchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
  name: z.string().trim().min(1).max(80),
});

export const LoginSchema = z.object({
  email: EmailSchema,
  password: z.string().min(1).max(200),
});

export type RegisterInput = z.infer<typeof RegisterSchema>;
export type LoginInput = z.infer<typeof LoginSchema>;

export const PublicUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: RoleSchema,
  createdAt: z.string(),
});

export type PublicUser = z.infer<typeof PublicUserSchema>;

export const AuthResponseSchema = z.object({
  user: PublicUserSchema,
  accessToken: z.string(),
  expiresIn: z.number().int(),
});

export type AuthResponse = z.infer<typeof AuthResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  Catalog                                                                   */
/* -------------------------------------------------------------------------- */

export const CategorySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  productCount: z.number().int().optional(),
});

export type Category = z.infer<typeof CategorySchema>;

export const ProductImageSchema = z.object({
  key: z.string(),
  alt: z.string(),
  url: z.string(),
  width: z.number().int().optional(),
  height: z.number().int().optional(),
});

export type ProductImage = z.infer<typeof ProductImageSchema>;

export const ProductSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  summary: z.string(),
  description: z.string(),
  priceMinor: z.number().int(),
  compareAtMinor: z.number().int().nullable(),
  currency: CurrencySchema,
  sku: z.string(),
  stock: z.number().int(),
  status: ProductStatusSchema,
  category: CategorySchema.nullable(),
  images: z.array(ProductImageSchema),
  tags: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Product = z.infer<typeof ProductSchema>;

export const ProductListSchema = z.object({
  items: z.array(ProductSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int(),
});

export type ProductList = z.infer<typeof ProductListSchema>;

export const ProductSortSchema = z
  .enum(['newest', 'price_asc', 'price_desc', 'name_asc', 'popular'])
  .default('newest');

export const ProductQuerySchema = PaginationSchema.extend({
  q: z.string().trim().max(120).optional(),
  category: z.string().max(80).optional(),
  minPrice: z.coerce.number().int().min(0).optional(),
  maxPrice: z.coerce.number().int().min(0).optional(),
  inStock: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
  sort: ProductSortSchema,
});

export type ProductQuery = z.infer<typeof ProductQuerySchema>;

export const UpsertProductSchema = z.object({
  name: z.string().trim().min(2).max(140),
  slug: SlugSchema,
  summary: z.string().trim().max(240).default(''),
  description: z.string().trim().max(8_000).default(''),
  priceMinor: MoneyMinorSchema,
  compareAtMinor: MoneyMinorSchema.nullable().default(null),
  currency: CurrencySchema.default('USD'),
  sku: z.string().trim().min(1).max(64),
  stock: z.number().int().min(0).max(1_000_000),
  status: ProductStatusSchema.default('draft'),
  categoryId: z.string().nullable().default(null),
  tags: z.array(z.string().trim().min(1).max(32)).max(12).default([]),
});

export type UpsertProductInput = z.infer<typeof UpsertProductSchema>;

export const UpsertCategorySchema = z.object({
  name: z.string().trim().min(2).max(80),
  slug: SlugSchema,
  description: z.string().trim().max(400).default(''),
});

/* -------------------------------------------------------------------------- */
/*  Cart                                                                      */
/* -------------------------------------------------------------------------- */

export const CartItemSchema = z.object({
  id: z.string(),
  productId: z.string(),
  slug: z.string(),
  name: z.string(),
  imageUrl: z.string().nullable(),
  unitPriceMinor: z.number().int(),
  quantity: z.number().int(),
  lineTotalMinor: z.number().int(),
  stock: z.number().int(),
  /** True when stock dropped below the quantity after the item was added. */
  stockWarning: z.boolean(),
});

export type CartItem = z.infer<typeof CartItemSchema>;

export const CartSchema = z.object({
  id: z.string(),
  items: z.array(CartItemSchema),
  itemCount: z.number().int(),
  totals: z.object({
    subtotalMinor: z.number().int(),
    shippingMinor: z.number().int(),
    taxMinor: z.number().int(),
    totalMinor: z.number().int(),
  }),
  currency: CurrencySchema,
  freeShippingThresholdMinor: z.number().int(),
});

export type Cart = z.infer<typeof CartSchema>;

export const AddToCartSchema = z.object({
  productId: z.string().min(1),
  quantity: z.number().int().min(1).max(20).default(1),
});

export const UpdateCartItemSchema = z.object({
  quantity: z.number().int().min(0).max(20),
});

/* -------------------------------------------------------------------------- */
/*  Checkout                                                                  */
/* -------------------------------------------------------------------------- */

export const AddressSchema = z.object({
  name: z.string().trim().min(1).max(120),
  line1: z.string().trim().min(1).max(160),
  line2: z.string().trim().max(160).default(''),
  city: z.string().trim().min(1).max(80),
  region: z.string().trim().max(80).default(''),
  postalCode: z.string().trim().min(1).max(24),
  country: z.string().trim().length(2, 'Use an ISO 3166-1 alpha-2 country code'),
  phone: z.string().trim().max(32).default(''),
});

export type Address = z.infer<typeof AddressSchema>;

export const CheckoutSchema = z.object({
  email: EmailSchema,
  shipping: AddressSchema,
  /** Present when the customer wants the order billed somewhere else. */
  billing: AddressSchema.nullable().default(null),
  /** Stripe-style test card number; only used by the mock provider. */
  paymentMethodToken: z.string().trim().min(3).max(64).default('tok_visa'),
  notes: z.string().trim().max(500).default(''),
});

export type CheckoutInput = z.infer<typeof CheckoutSchema>;

export const OrderItemSchema = z.object({
  id: z.string(),
  productId: z.string().nullable(),
  slug: z.string().nullable(),
  name: z.string(),
  imageUrl: z.string().nullable(),
  unitPriceMinor: z.number().int(),
  quantity: z.number().int(),
  lineTotalMinor: z.number().int(),
});

export const OrderSchema = z.object({
  id: z.string(),
  number: z.string(),
  status: OrderStatusSchema,
  email: z.string(),
  currency: CurrencySchema,
  subtotalMinor: z.number().int(),
  shippingMinor: z.number().int(),
  taxMinor: z.number().int(),
  totalMinor: z.number().int(),
  shippingAddress: AddressSchema,
  items: z.array(OrderItemSchema),
  paymentReference: z.string().nullable(),
  placedAt: z.string(),
  updatedAt: z.string(),
});

export type Order = z.infer<typeof OrderSchema>;

export const OrderSummarySchema = OrderSchema.pick({
  id: true,
  number: true,
  status: true,
  totalMinor: true,
  currency: true,
  placedAt: true,
}).extend({ itemCount: z.number().int() });

export type OrderSummary = z.infer<typeof OrderSummarySchema>;

/* -------------------------------------------------------------------------- */
/*  Admin                                                                     */
/* -------------------------------------------------------------------------- */

export const UpdateOrderStatusSchema = z.object({
  status: OrderStatusSchema,
  note: z.string().trim().max(300).default(''),
});

export const AdjustStockSchema = z.object({
  /** Positive to receive stock, negative to write off damaged or lost units. */
  delta: z.number().int().min(-10_000).max(10_000).refine((v) => v !== 0, 'Delta cannot be zero'),
  reason: z.enum(['receive', 'write_off', 'correction', 'return']),
  note: z.string().trim().max(300).default(''),
});

export const DashboardMetricsSchema = z.object({
  revenueMinor: z.number().int(),
  revenueChangeBps: z.number().int(),
  orderCount: z.number().int(),
  orderChangeBps: z.number().int(),
  averageOrderValueMinor: z.number().int(),
  lowStockCount: z.number().int(),
  pendingCount: z.number().int(),
  revenueByDay: z.array(z.object({ date: z.string(), revenueMinor: z.number().int() })),
  topProducts: z.array(
    z.object({ name: z.string(), slug: z.string(), unitsSold: z.number().int(), revenueMinor: z.number().int() }),
  ),
});

export type DashboardMetrics = z.infer<typeof DashboardMetricsSchema>;

/* -------------------------------------------------------------------------- */
/*  Errors                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * RFC 9457 problem details. Every non-2xx response from the API has this shape,
 * which means the client has exactly one error-parsing path.
 */
export const ProblemSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string(),
  instance: z.string().optional(),
  requestId: z.string().optional(),
  errors: z
    .array(z.object({ path: z.string(), message: z.string(), code: z.string().optional() }))
    .optional(),
});

export type Problem = z.infer<typeof ProblemSchema>;
